import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Common port coordinates database (sea-route fast path).
// For anything not in this table we fall through to a Nominatim
// (OpenStreetMap) geocoder, which covers inland/road locations like
// "Dekani" or "Tirana".
const PORT_COORDINATES = {
  // China
  'shanghai': { lat: 31.23, lng: 121.47 },
  'ningbo': { lat: 29.87, lng: 121.55 },
  'shenzhen': { lat: 22.54, lng: 114.06 },
  'qingdao': { lat: 36.07, lng: 120.38 },
  'tianjin': { lat: 39.08, lng: 117.71 },
  'xiamen': { lat: 24.48, lng: 118.09 },
  'dalian': { lat: 38.92, lng: 121.64 },
  'guangzhou': { lat: 23.13, lng: 113.27 },
  // Southeast Asia
  'singapore': { lat: 1.29, lng: 103.85 },
  'port klang': { lat: 3.00, lng: 101.39 },
  'tanjung pelepas': { lat: 1.37, lng: 103.55 },
  'ho chi minh': { lat: 10.82, lng: 106.63 },
  'bangkok': { lat: 13.76, lng: 100.50 },
  'jakarta': { lat: -6.11, lng: 106.88 },
  // South Asia
  'colombo': { lat: 6.93, lng: 79.84 },
  'chennai': { lat: 13.08, lng: 80.27 },
  'mumbai': { lat: 18.94, lng: 72.84 },
  'nhava sheva': { lat: 18.95, lng: 72.95 },
  // Middle East
  'jebel ali': { lat: 25.02, lng: 55.06 },
  'dubai': { lat: 25.27, lng: 55.29 },
  'port said': { lat: 31.26, lng: 32.30 },
  'suez': { lat: 29.97, lng: 32.54 },
  // Europe
  'koper': { lat: 45.55, lng: 13.73 },
  'trieste': { lat: 45.65, lng: 13.78 },
  'rijeka': { lat: 45.33, lng: 14.44 },
  'piraeus': { lat: 37.94, lng: 23.64 },
  'genoa': { lat: 44.41, lng: 8.93 },
  'rotterdam': { lat: 51.90, lng: 4.50 },
  'hamburg': { lat: 53.55, lng: 9.99 },
  'antwerp': { lat: 51.22, lng: 4.40 },
  'felixstowe': { lat: 51.95, lng: 1.31 },
  'le havre': { lat: 49.49, lng: 0.11 },
  'barcelona': { lat: 41.35, lng: 2.17 },
  'valencia': { lat: 39.45, lng: -0.32 },
  'algeciras': { lat: 36.13, lng: -5.44 },
  // Inland / road hubs we hit often
  'dekani': { lat: 45.527, lng: 13.789 },
  'tirana': { lat: 41.328, lng: 19.819 },
  'ljubljana': { lat: 46.056, lng: 14.506 },
  'zagreb': { lat: 45.815, lng: 15.982 },
  'belgrade': { lat: 44.787, lng: 20.457 },
  'sofia': { lat: 42.698, lng: 23.319 },
  // Americas
  'los angeles': { lat: 33.74, lng: -118.26 },
  'long beach': { lat: 33.77, lng: -118.19 },
  'new york': { lat: 40.68, lng: -74.04 },
  'savannah': { lat: 32.08, lng: -81.10 },
  'houston': { lat: 29.76, lng: -95.36 },
};

// Sea route waypoints for common routes
const ROUTE_WAYPOINTS = {
  shanghai_koper: [
    { name: 'Shanghai', lat: 31.23, lng: 121.47, type: 'origin' },
    { name: 'Taiwan Strait', lat: 24.5, lng: 118.5, type: 'waypoint' },
    { name: 'South China Sea', lat: 12.0, lng: 112.0, type: 'waypoint' },
    { name: 'Singapore', lat: 1.29, lng: 103.85, type: 'port' },
    { name: 'Malacca Strait', lat: 4.0, lng: 98.0, type: 'waypoint' },
    { name: 'Indian Ocean', lat: 5.0, lng: 85.0, type: 'waypoint' },
    { name: 'Colombo', lat: 6.93, lng: 79.84, type: 'port' },
    { name: 'Arabian Sea', lat: 12.0, lng: 60.0, type: 'waypoint' },
    { name: 'Gulf of Aden', lat: 12.5, lng: 48.0, type: 'waypoint' },
    { name: 'Red Sea', lat: 20.0, lng: 38.5, type: 'waypoint' },
    { name: 'Suez', lat: 29.97, lng: 32.54, type: 'port' },
    { name: 'Mediterranean', lat: 35.0, lng: 20.0, type: 'waypoint' },
    { name: 'Adriatic', lat: 42.0, lng: 16.0, type: 'waypoint' },
    { name: 'Koper', lat: 45.55, lng: 13.73, type: 'destination' }
  ]
};

// ─── Geocoder ─────────────────────────────────────────────────────────
// First: direct match in the port dict. Then: a partial substring match.
// Finally: hit Nominatim with a localStorage-cached query so we don't
// hammer the free service (their policy is 1 req/sec; cache survives
// page reloads so a given location only resolves once).
const _portLookup = (name) => {
  if (!name) return null;
  const n = name.toLowerCase().trim();
  if (PORT_COORDINATES[n]) return PORT_COORDINATES[n];
  for (const [k, c] of Object.entries(PORT_COORDINATES)) {
    if (n.includes(k) || k.includes(n)) return c;
  }
  return null;
};

const _cacheKey = (q) => `geo-cache:v2:${q.trim().toLowerCase()}`;

async function geocode(rawName) {
  if (!rawName) return null;

  // Pull a clean "City, Country" hint from anywhere in the text — this is
  // by far the most reliable signal for Nominatim. We try this BEFORE the
  // hardcoded port-dict lookup so a multi-line TO address like
  //   "GSA warehouse, Location (https://maps.app.goo.gl/...)
  //    Aldi MUCKA, Electrical Engineering
  //    Rr. Ibrahim Rugova, Pallati 64/1.
  //    Tirana, Albania
  //    Tel: +355..."
  // resolves to *Tirana, Albania*, not to a random "GSA warehouse" match
  // that Nominatim happens to find in the Caribbean.
  const COUNTRIES = (
    'albania|austria|belgium|bosnia|bulgaria|china|croatia|czech|denmark|'
    + 'egypt|estonia|france|germany|greece|hungary|india|ireland|italy|'
    + 'latvia|lithuania|luxembourg|netherlands|norway|poland|portugal|'
    + 'romania|serbia|singapore|slovakia|slovenia|spain|sweden|'
    + 'switzerland|taiwan|turkey|ukraine|united kingdom|uk|usa|united states|'
    + 'macedonia|kosovo|montenegro|moldova|cyprus|finland|iceland|'
    + 'argentina|brazil|mexico|japan|south korea|vietnam|thailand|malaysia|'
    + 'indonesia|philippines|south africa|morocco|tunisia|algeria|'
    + 'saudi arabia|uae|qatar|kuwait|israel|iran|iraq|jordan|lebanon|'
    + 'pakistan|bangladesh|sri lanka|nepal|new zealand|australia|canada|'
    + 'norway|nigeria|kenya|ethiopia'
  );
  const cityCountryRx = new RegExp(
    `\\b([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ '\\-]{1,40})\\s*,\\s*(${COUNTRIES})\\b`,
    'i',
  );
  const cc = rawName.match(cityCountryRx);
  if (cc) {
    const cleanQuery = `${cc[1].trim()}, ${cc[2].trim()}`;
    const portHit = _portLookup(cleanQuery) || _portLookup(cc[1].trim());
    if (portHit) return portHit;
    const key = _cacheKey(cleanQuery);
    try {
      const cached = localStorage.getItem(key);
      if (cached !== null) {
        const parsed = JSON.parse(cached);
        if (parsed) return parsed;
      }
    } catch { /* ignore */ }
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(cleanQuery)}`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        const arr = await res.json();
        if (arr && arr[0]) {
          const hit = { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) };
          try { localStorage.setItem(key, JSON.stringify(hit)); } catch { /* quota */ }
          return hit;
        }
      }
    } catch (e) {
      console.warn(`geocode (city,country) failed for "${cleanQuery}":`, e);
    }
  }

  // No clean city,country pattern — try the port dictionary first.
  const portHit = _portLookup(rawName);
  if (portHit) return portHit;

  // Last resort: clean the raw text aggressively and ask Nominatim.
  let q = rawName
    .replace(/https?:\/\/\S+/g, ' ')                       // strip URLs
    .replace(/\b(?:tel|telephone|phone|mob|mobile|fax)[.: ]+[+\d() \-]+/gi, ' ')
    .replace(/\b\+?\d[\d \-()]{6,}\b/g, ' ')               // strip bare phone numbers
    .replace(/\b\d{1,2}:\d{2}\s*[-–]\s*\d{1,2}:\d{2}\b/g, ' ')  // strip "8:00 - 16:00"
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[\r\n]+/g, ', ')                             // multi-line → comma-sep
    .replace(/[^\p{L}\p{N},.'\- /]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!q) return null;
  // Cap absurd queries
  if (q.length > 120) q = q.slice(0, 120);

  const key = _cacheKey(q);
  try {
    const cached = localStorage.getItem(key);
    if (cached !== null) {
      const parsed = JSON.parse(cached);
      return parsed || null;
    }
  } catch { /* ignore quota / parse errors */ }

  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`geocode ${res.status}`);
    const arr = await res.json();
    const hit = (arr && arr[0])
      ? { lat: parseFloat(arr[0].lat), lng: parseFloat(arr[0].lon) }
      : null;
    try { localStorage.setItem(key, JSON.stringify(hit)); }
    catch { /* quota */ }
    return hit;
  } catch (e) {
    console.warn(`geocode failed for "${q}":`, e);
    return null;
  }
}

// Generate sea-route waypoints between two known ports
const generateRouteWaypoints = (originCoords, destCoords, originName, destName) => {
  const waypoints = [
    { name: originName, lat: originCoords.lat, lng: originCoords.lng, type: 'origin' }
  ];
  // Add intermediate waypoints for Asia-to-Europe via Suez
  if (originCoords.lng > 60 && destCoords.lng < 40) {
    if (originCoords.lng > 100) {
      waypoints.push({ name: 'Singapore', lat: 1.29, lng: 103.85, type: 'port' });
    }
    waypoints.push({ name: 'Colombo', lat: 6.93, lng: 79.84, type: 'port' });
    waypoints.push({ name: 'Suez', lat: 29.97, lng: 32.54, type: 'port' });
  }
  waypoints.push({ name: destName, lat: destCoords.lat, lng: destCoords.lng, type: 'destination' });
  return waypoints;
};

const createMarkerIcon = (type) => {
  const colors = {
    origin: '#003366',
    destination: '#10b981',
    port: '#00a0dc',
    waypoint: '#94a3b8',
    current: '#f59e0b',
    ping: '#7c3aed',
    ping_latest: '#dc2626',
  };
  const size = (type === 'origin' || type === 'destination' || type === 'current' || type === 'ping_latest')
    ? 14 : type === 'port' || type === 'ping' ? 10 : 0;
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background: ${colors[type]};
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      ${(type === 'current' || type === 'ping_latest') ? 'animation: pulse 2s infinite;' : ''}
    "></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

const createShipIcon = () => L.divIcon({
  className: 'ship-marker',
  html: `<div style="width:24px;height:24px;background:#f59e0b;border:2px solid white;border-radius:50%;box-shadow:0 2px 8px rgba(245,158,11,0.5);display:flex;align-items:center;justify-content:center;font-size:12px;">🚢</div>`,
  iconSize: [24, 24], iconAnchor: [12, 12],
});

const ShippingRouteMap = ({
  route = 'shanghai_koper',
  height = '340px',
  showLabels = true,
  animateShip = true,
  progress = 100,
  originPort = null,
  destinationPort = null,
  currentLocation = null,
  containerNumber = null,
  // NEW: an array of `{location: {latitude, longitude, address, timestamp},
  //                    status, responded_at, requested_at}` rows.
  // The latest ping (by responded_at) is highlighted; the others are
  // shown as smaller markers with a connecting polyline so you can see
  // the truck's path.
  trackingPings = [],
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const shipMarkerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [waypoints, setWaypoints] = useState(null);
  const [geocodeError, setGeocodeError] = useState(null);

  // ── Resolve origin/destination coordinates (async because of Nominatim) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!originPort || !destinationPort) {
        if (!cancelled) setWaypoints(ROUTE_WAYPOINTS[route] || ROUTE_WAYPOINTS.shanghai_koper);
        return;
      }
      try {
        const [oc, dc] = await Promise.all([geocode(originPort), geocode(destinationPort)]);
        if (cancelled) return;
        if (!oc || !dc) {
          setGeocodeError(
            !oc && !dc ? `Couldn't locate "${originPort}" or "${destinationPort}"`
            : !oc ? `Couldn't locate "${originPort}"`
                  : `Couldn't locate "${destinationPort}"`,
          );
          // Still render with whichever side we got
          if (oc && dc) {
            setWaypoints(generateRouteWaypoints(oc, dc, originPort, destinationPort));
          } else if (oc) {
            setWaypoints([{ name: originPort, lat: oc.lat, lng: oc.lng, type: 'origin' }]);
          } else if (dc) {
            setWaypoints([{ name: destinationPort, lat: dc.lat, lng: dc.lng, type: 'destination' }]);
          } else {
            setWaypoints([]);
          }
          return;
        }
        setGeocodeError(null);
        setWaypoints(generateRouteWaypoints(oc, dc, originPort, destinationPort));
      } catch (e) {
        if (!cancelled) {
          setGeocodeError(`Geocoder error: ${e.message}`);
          setWaypoints([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [originPort, destinationPort, route]);

  // ── Build / rebuild the Leaflet map every time the waypoints or
  //    tracking pings change. ────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    if (waypoints === null) return; // still geocoding
    // Tear down any previous instance — we always rebuild on prop change
    // so re-renders don't leave stale markers behind.
    if (mapInstanceRef.current) {
      try { mapInstanceRef.current.remove(); } catch { /* ignore */ }
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      zoomControl: true,
      scrollWheelZoom: false,
      dragging: true,
      attributionControl: false,
    });
    mapInstanceRef.current = map;
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);
    L.control.attribution({ prefix: false, position: 'bottomright' })
      .addTo(map).addAttribution('© OpenStreetMap');

    // ── Origin → destination route line ──
    const routeCoords = waypoints.map(wp => [wp.lat, wp.lng]);
    if (routeCoords.length >= 2) {
      for (let i = 0; i < routeCoords.length - 1; i++) {
        L.polyline([routeCoords[i], routeCoords[i + 1]], {
          color: `hsl(${200 + (i * 10)}, 70%, ${40 + (i * 2)}%)`,
          weight: 4, opacity: 0.8, dashArray: '10, 6',
          lineCap: 'round', lineJoin: 'round',
        }).addTo(map);
      }
    }

    waypoints.forEach((wp) => {
      if (wp.type === 'waypoint') return;
      const marker = L.marker([wp.lat, wp.lng], { icon: createMarkerIcon(wp.type) }).addTo(map);
      if (showLabels && (wp.type === 'origin' || wp.type === 'destination' || wp.type === 'port')) {
        marker.bindTooltip(wp.name, {
          permanent: true,
          direction: wp.type === 'origin' ? 'right' : wp.type === 'destination' ? 'left' : 'top',
          className: 'route-label', offset: [0, -10],
        });
      }
    });

    // ── Tracking pings (the SMS-driver-location feed) ──
    // Filter to pings that actually have GPS coordinates, sort
    // chronologically, draw a path, and highlight the most recent.
    const pingsWithGPS = (trackingPings || [])
      .map((p) => ({
        ...p,
        _lat: p?.location?.latitude,
        _lng: p?.location?.longitude,
        _t: p?.responded_at || p?.last_update || p?.location?.timestamp || p?.requested_at,
      }))
      .filter(p => typeof p._lat === 'number' && typeof p._lng === 'number')
      .sort((a, b) => (new Date(a._t || 0)) - (new Date(b._t || 0)));

    if (pingsWithGPS.length >= 2) {
      L.polyline(pingsWithGPS.map(p => [p._lat, p._lng]), {
        color: '#7c3aed', weight: 3, opacity: 0.85,
        lineCap: 'round', lineJoin: 'round',
      }).addTo(map);
    }

    pingsWithGPS.forEach((p, i) => {
      const latest = i === pingsWithGPS.length - 1;
      const marker = L.marker([p._lat, p._lng], {
        icon: createMarkerIcon(latest ? 'ping_latest' : 'ping'),
        zIndexOffset: latest ? 900 : 500,
      }).addTo(map);
      const when = p._t ? new Date(p._t).toLocaleString() : '';
      const addr = p?.location?.address || '';
      marker.bindPopup(
        `<div style="font-size:12px"><b>${latest ? '📍 Latest driver location' : '📍 Driver location'}</b>`
        + (addr ? `<br/>${addr.replace(/</g, '&lt;')}` : '')
        + (when ? `<br/><span style="color:#666">${when}</span>` : '')
        + `</div>`
      );
      if (latest) marker.openPopup();
    });

    // Animated sea-route ship — keep ONLY for predefined sea routes,
    // never for trucks (animateShip prop already false in road UI).
    if (animateShip && routeCoords.length >= 2) {
      const progressIndex = Math.floor((progress / 100) * (routeCoords.length - 1));
      const shipPos = routeCoords[Math.min(progressIndex, routeCoords.length - 1)];
      shipMarkerRef.current = L.marker(shipPos, {
        icon: createShipIcon(), zIndexOffset: 1000,
      }).addTo(map);
      let currentIndex = 0;
      const animateStep = () => {
        if (!shipMarkerRef.current) return;
        if (currentIndex < routeCoords.length - 1) {
          currentIndex++;
          shipMarkerRef.current.setLatLng(routeCoords[currentIndex]);
          setTimeout(animateStep, 300);
        } else {
          setTimeout(() => {
            currentIndex = 0;
            if (shipMarkerRef.current) shipMarkerRef.current.setLatLng(routeCoords[0]);
            setTimeout(animateStep, 1000);
          }, 2000);
        }
      };
      setTimeout(animateStep, 1000);
    }

    // ── Fit map to the union of route + ping markers ──
    const allPoints = [
      ...routeCoords,
      ...pingsWithGPS.map(p => [p._lat, p._lng]),
    ];
    if (allPoints.length === 0) {
      // No data at all — centre on Europe so the user at least sees the map.
      map.setView([45.0, 14.0], 5);
    } else if (allPoints.length === 1) {
      map.setView(allPoints[0], 10);
    } else {
      map.fitBounds(L.latLngBounds(allPoints), { padding: [30, 30] });
    }

    setIsLoaded(true);
    return () => {
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch { /* ignore */ }
        mapInstanceRef.current = null;
      }
      shipMarkerRef.current = null;
    };
  }, [waypoints, animateShip, progress, showLabels, trackingPings]);

  return (
    <div className="relative" style={{ height }} data-testid="shipping-route-map">
      <div ref={mapRef} className="w-full h-full rounded-lg" />
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}
      {containerNumber && (
        <div className="absolute top-2 left-2 bg-white/90 px-3 py-1 rounded-lg shadow-sm z-[1000]">
          <span className="text-xs font-mono font-semibold text-gray-700">{containerNumber}</span>
        </div>
      )}
      {geocodeError && (
        <div className="absolute bottom-2 left-2 right-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs px-2 py-1 rounded z-[1000]"
              data-testid="map-geocode-warning">
          ⚠ {geocodeError}
        </div>
      )}
      <style>{`
        .route-label {
          background: white; border: none;
          box-shadow: 0 2px 4px rgba(0,0,0,0.15);
          padding: 2px 8px; font-size: 11px; font-weight: 600;
          color: #1e3a5f; border-radius: 4px;
        }
        .leaflet-tooltip-left:before, .leaflet-tooltip-right:before,
        .leaflet-tooltip-top:before, .leaflet-tooltip-bottom:before { border: none; }
        @keyframes pulse {
          0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(245,158,11,.7); }
          70%  { transform: scale(1.15);box-shadow: 0 0 0 10px rgba(245,158,11,0); }
          100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(245,158,11,0); }
        }
      `}</style>
    </div>
  );
};

export default ShippingRouteMap;
